import { Box, Grid, SvgIcon, Typography } from '@mui/material';
import { useNewQuoteStore } from '@store/NewQuote';
import { Address } from '@components/address';
import { InfoCircle } from '@assets/svgs';
import { EnteredAddress } from '@models/validation';
import { Tooltip } from '@components/tooltip/Tooltip';
import { useGetScreenSize } from '@utils/useGetScreenSize';

interface ITypeAheadEntry {
  hoverText: string;
  onChangeAddressEntry: () => void;
  setIsLoading: (l: boolean) => void;
  stateList: string[];
  updateAddress: (addressToUpdate: EnteredAddress) => void;
  /**
   * When false, withholds `stateList` from the underlying `Address` component so it skips its
   * jurisdiction-availability check specifically, while `Address`'s other validation (physical-address
   * loading/match/error feedback) stays intact. `hoverText`/`stateList` are still shown informationally
   * via the tooltip regardless. Default true.
   */
  shouldValidateJurisdiction?: boolean;
}

export const TypeAheadEntry = ({
  hoverText,
  onChangeAddressEntry,
  setIsLoading,
  stateList,
  updateAddress,
  shouldValidateJurisdiction = true,
}: ITypeAheadEntry) => {
  const { location, scope } = useNewQuoteStore();
  const { insuredLocation } = location;
  const { isSpecialEvent } = scope;
  const { isMobileOrSmallScreen } = useGetScreenSize();
  return (
    <>
      <Grid
        size={{ xs: 12 }}
        sx={{
          paddingBottom: '8px',
        }}
      >
        <Box sx={{ justifyContent: 'center', display: 'flex', flexDirection: 'row', marginBottom: 2 }}>
          <Typography variant='bodySmall'>
            {isSpecialEvent
              ? 'Please enter the address of the event venue'
              : 'Please enter the primary location that this policy will cover.'}
          </Typography>
          <Tooltip
            placement='bottom'
            tooltipBgColor='common.white'
            tooltipTextColor='common.black'
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant='bodySmall'
                  sx={{
                    color: 'common.black',
                    marginBottom: '12px',
                  }}
                >
                  {hoverText}
                </Typography>
                <Typography
                  variant='bodySmall'
                  sx={{
                    color: 'common.black',
                  }}
                >
                  {stateList.join(', ')}
                </Typography>
              </Box>
            }
            sx={{
              maxHeight: 'none',
            }}
          >
            <SvgIcon
              sx={{
                cursor: 'pointer',
                width: '18px',
                height: '18px',
                marginTop: '2px',
                marginLeft: '4px',
                color: 'common.white',
              }}
            >
              <InfoCircle />
            </SvgIcon>
          </Tooltip>
        </Box>
      </Grid>
      <Grid
        container
        rowSpacing='34px'
        sx={{
          width: isMobileOrSmallScreen ? '100%' : '500px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Grid size={{ xs: 12 }} key={`${insuredLocation.street1}${insuredLocation.zipCode}`}>
          <Address
            label={isSpecialEvent ? 'Address of Event Venue' : 'Insured Location'}
            address={insuredLocation}
            setIsLoading={setIsLoading}
            onChangeAddressEntry={() => {
              onChangeAddressEntry();
            }}
            onSelectAddress={updateAddress}
            stateList={shouldValidateJurisdiction ? stateList : undefined}
          />
        </Grid>
      </Grid>
    </>
  );
};
